import React, { useState, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
  HelpCircle,
  ChevronDown,
  ChevronUp,
  Home,
  MapPin,
  GraduationCap,
  Heart,
  Shield,
  Bell,
  Search,
  Settings,
  UserPlus,
  Share2,
  X,
  MessageCircle,
  Calendar,
  Users,
  ShoppingBag,
  TrendingUp
} from 'lucide-react';
import GuideLanguageSelector from './GuideLanguageSelector';

const ManualTrans: React.FC<{ i18nKey: string }> = ({ i18nKey }) => (
  <Trans i18nKey={i18nKey} components={{ 0: <strong /> }} />
);

const UserManual: React.FC = () => {
  const { t } = useTranslation();
  const [showUserManual, setShowUserManual] = useState(false);
  const [expandedSections, setExpandedSections] = useState<string[]>(['getting-started']);
  useEffect(() => {
    const seen = localStorage.getItem('kinship-manual-seen');
    if (!seen) {
      setShowUserManual(true);
    }
  }, []);

  useEffect(() => {
    const handleOpenManual = () => {
      setShowUserManual(true);
    };

    window.addEventListener('open-help-guide', handleOpenManual);
    return () => window.removeEventListener('open-help-guide', handleOpenManual);
  }, []);

  const handleClose = () => {
    setShowUserManual(false);
    localStorage.setItem('kinship-manual-seen', 'true');
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev =>
      prev.includes(sectionId)
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  if (!showUserManual) {
    return (
      <button
        onClick={() => setShowUserManual(true)}
        className="fixed bottom-24 lg:bottom-4 right-4 z-40 flex items-center gap-2 px-4 py-3 rounded-full bg-gradient-to-r from-amber-500 to-orange-600 text-white shadow-lg shadow-amber-500/30 hover:shadow-amber-500/50 hover:scale-105 transition-all duration-300"
        title={t('userManual.openTitle')}
      >
        <HelpCircle className="w-5 h-5" />
        <span className="font-medium">{t('userManual.helpGuide')}</span>
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8">
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={handleClose}
        aria-hidden
      />

      <div className="relative w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-2xl border border-amber-500/30 bg-gradient-to-br from-[#1a1510] via-dark-card to-[#15120d] shadow-2xl shadow-amber-900/20">
        <div className="absolute top-0 right-0 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-orange-500/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2 pointer-events-none" />

        <div className="relative px-6 py-5 border-b border-amber-500/20 bg-gradient-to-r from-amber-950/50 to-orange-950/30">
          <div className="absolute top-4 right-4 flex items-center gap-2">
            <GuideLanguageSelector variant="header" />
            <button
              type="button"
              onClick={handleClose}
              className="p-2 rounded-full bg-dark-bg/50 hover:bg-dark-bg text-text-muted hover:text-white transition-all"
              title={t('userManual.close')}
              aria-label={t('userManual.close')}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex items-center gap-4 pr-36 md:pr-44">
            <div className="p-3 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 shadow-lg shadow-amber-500/30">
              <HelpCircle className="w-8 h-8 text-white" />
            </div>
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-400">
                {t('userManual.title')}
              </h2>
              <p className="text-amber-200/70 text-sm md:text-base">{t('userManual.subtitle')}</p>
            </div>
          </div>
        </div>

        <div className="relative overflow-y-auto max-h-[calc(90vh-180px)] p-6">
          <div className="mb-6 p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
            <p className="text-text-secondary leading-relaxed">
              <Trans
                i18nKey="userManual.overview"
                components={{ 0: <span className="text-amber-400 font-semibold" /> }}
              />
            </p>
          </div>

          <div className="space-y-3">
            {/* Getting Started */}
            <div className="rounded-xl border border-dark-border/50 overflow-hidden bg-dark-bg/50">
              <button
                type="button"
                onClick={() => toggleSection('getting-started')}
                className="w-full flex items-center justify-between p-4 hover:bg-dark-card/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-green-500/20">
                    <UserPlus className="w-5 h-5 text-green-400" />
                  </div>
                  <span className="font-semibold text-text-primary">{t('userManual.gettingStarted')}</span>
                </div>
                {expandedSections.includes('getting-started') ? (
                  <ChevronUp className="w-5 h-5 text-text-muted" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-text-muted" />
                )}
              </button>
              {expandedSections.includes('getting-started') && (
                <div className="px-4 pb-4 space-y-3 border-t border-dark-border/30">
                  <div className="pt-4 grid gap-3">
                    {[
                      { title: 'userManual.step1Title', desc: 'userManual.step1Desc' },
                      { title: 'userManual.step2Title', desc: 'userManual.step2Desc' },
                      { title: 'userManual.step3Title', desc: 'userManual.step3Desc' },
                    ].map((step, index) => (
                      <div key={step.title} className="flex gap-3 p-3 rounded-lg bg-dark-card/50">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500/20 text-green-400 flex items-center justify-center text-sm font-bold">
                          {index + 1}
                        </span>
                        <div>
                          <h4 className="font-medium text-text-primary">{t(step.title)}</h4>
                          <p className="text-sm text-text-secondary">{t(step.desc)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Navigation */}
            <div className="rounded-xl border border-dark-border/50 overflow-hidden bg-dark-bg/50">
              <button
                type="button"
                onClick={() => toggleSection('navigation')}
                className="w-full flex items-center justify-between p-4 hover:bg-dark-card/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-accent-blue/20">
                    <Home className="w-5 h-5 text-accent-blue" />
                  </div>
                  <span className="font-semibold text-text-primary">{t('userManual.navigation')}</span>
                </div>
                {expandedSections.includes('navigation') ? (
                  <ChevronUp className="w-5 h-5 text-text-muted" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-text-muted" />
                )}
              </button>
              {expandedSections.includes('navigation') && (
                <div className="px-4 pb-4 border-t border-dark-border/30">
                  <div className="pt-4 space-y-4">
                    <div>
                      <h4 className="font-medium text-amber-400 mb-2 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-amber-400" />
                        {t('userManual.sidebarMenu')}
                      </h4>
                      <ul className="space-y-2 text-sm text-text-secondary ml-4">
                        <li className="flex items-center gap-2">
                          <TrendingUp className="w-4 h-4 text-accent-blue shrink-0" />
                          <ManualTrans i18nKey="userManual.globalFeed" />
                        </li>
                        <li className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-purple-400 shrink-0" />
                          <ManualTrans i18nKey="userManual.communityEvents" />
                        </li>
                        <li className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-green-400 shrink-0" />
                          <ManualTrans i18nKey="userManual.communityMembers" />
                        </li>
                        <li className="flex items-center gap-2">
                          <MessageCircle className="w-4 h-4 text-cyan-400 shrink-0" />
                          <ManualTrans i18nKey="userManual.culturalPosts" />
                        </li>
                        <li className="flex items-center gap-2">
                          <GraduationCap className="w-4 h-4 text-orange-400 shrink-0" />
                          <ManualTrans i18nKey="userManual.learningHub" />
                        </li>
                        <li className="flex items-center gap-2">
                          <ShoppingBag className="w-4 h-4 text-pink-400 shrink-0" />
                          <ManualTrans i18nKey="userManual.marketplaceNav" />
                        </li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-medium text-amber-400 mb-2 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-amber-400" />
                        {t('userManual.headerBar')}
                      </h4>
                      <ul className="space-y-2 text-sm text-text-secondary ml-4">
                        <li className="flex items-center gap-2">
                          <Search className="w-4 h-4 text-gray-400 shrink-0" />
                          <ManualTrans i18nKey="userManual.search" />
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-lg shrink-0">🌐</span>
                          <ManualTrans i18nKey="userManual.languageSelector" />
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-lg shrink-0">🎵</span>
                          <ManualTrans i18nKey="userManual.musicControl" />
                        </li>
                        <li className="flex items-center gap-2">
                          <Bell className="w-4 h-4 text-yellow-400 shrink-0" />
                          <ManualTrans i18nKey="userManual.notifications" />
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Kinship Groups */}
            <div className="rounded-xl border border-dark-border/50 overflow-hidden bg-dark-bg/50">
              <button
                type="button"
                onClick={() => toggleSection('kinship')}
                className="w-full flex items-center justify-between p-4 hover:bg-dark-card/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-purple-500/20">
                    <Heart className="w-5 h-5 text-purple-400" />
                  </div>
                  <span className="font-semibold text-text-primary">{t('userManual.kinshipGroups')}</span>
                </div>
                {expandedSections.includes('kinship') ? (
                  <ChevronUp className="w-5 h-5 text-text-muted" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-text-muted" />
                )}
              </button>
              {expandedSections.includes('kinship') && (
                <div className="px-4 pb-4 border-t border-dark-border/30">
                  <div className="pt-4 space-y-4">
                    <p className="text-sm text-text-secondary">{t('userManual.kinshipIntro')}</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="p-3 rounded-lg bg-dark-card/50 border border-accent-blue/20">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-2xl">🦅</span>
                          <h4 className="font-medium text-accent-blue">{t('userManual.thunderbird')}</h4>
                        </div>
                        <p className="text-xs text-text-secondary">{t('userManual.thunderbirdDesc')}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-dark-card/50 border border-green-500/20">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-2xl">🌳</span>
                          <h4 className="font-medium text-green-400">{t('userManual.oakTree')}</h4>
                        </div>
                        <p className="text-xs text-text-secondary">{t('userManual.oakTreeDesc')}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-dark-card/50 border border-cyan-500/20">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-2xl">🐋</span>
                          <h4 className="font-medium text-cyan-400">{t('userManual.whale')}</h4>
                        </div>
                        <p className="text-xs text-text-secondary">{t('userManual.whaleDesc')}</p>
                      </div>
                    </div>
                    <p className="text-xs text-amber-400/80 italic">{t('userManual.kinshipTip')}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Features */}
            <div className="rounded-xl border border-dark-border/50 overflow-hidden bg-dark-bg/50">
              <button
                type="button"
                onClick={() => toggleSection('features')}
                className="w-full flex items-center justify-between p-4 hover:bg-dark-card/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-cyan-500/20">
                    <Settings className="w-5 h-5 text-cyan-400" />
                  </div>
                  <span className="font-semibold text-text-primary">{t('userManual.features')}</span>
                </div>
                {expandedSections.includes('features') ? (
                  <ChevronUp className="w-5 h-5 text-text-muted" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-text-muted" />
                )}
              </button>
              {expandedSections.includes('features') && (
                <div className="px-4 pb-4 border-t border-dark-border/30">
                  <div className="pt-4 space-y-4">
                    <div className="p-3 rounded-lg bg-dark-card/50">
                      <h4 className="font-medium text-text-primary mb-2 flex items-center gap-2">
                        <MessageCircle className="w-4 h-4 text-accent-blue" /> {t('userManual.creatingPosts')}
                      </h4>
                      <ul className="text-sm text-text-secondary space-y-1 ml-6 list-disc">
                        {['creatingPosts1', 'creatingPosts2', 'creatingPosts3', 'creatingPosts4', 'creatingPosts5'].map((key) => (
                          <li key={key}>{t(`userManual.${key}`)}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="p-3 rounded-lg bg-dark-card/50">
                      <h4 className="font-medium text-text-primary mb-2 flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-purple-400" /> {t('userManual.managingEvents')}
                      </h4>
                      <ul className="text-sm text-text-secondary space-y-1 ml-6 list-disc">
                        {['managingEvents1', 'managingEvents2', 'managingEvents3', 'managingEvents4', 'managingEvents5'].map((key) => (
                          <li key={key}>{t(`userManual.${key}`)}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="p-3 rounded-lg bg-dark-card/50">
                      <h4 className="font-medium text-text-primary mb-2 flex items-center gap-2">
                        <Share2 className="w-4 h-4 text-green-400" /> {t('userManual.socialFeatures')}
                      </h4>
                      <ul className="text-sm text-text-secondary space-y-1 ml-6 list-disc">
                        {['socialFriends', 'socialMessaging', 'socialComments', 'socialProfile'].map((key) => (
                          <li key={key}>
                            <ManualTrans i18nKey={`userManual.${key}`} />
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="p-3 rounded-lg bg-dark-card/50">
                      <h4 className="font-medium text-text-primary mb-2 flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-red-400" /> {t('userManual.communityMap')}
                      </h4>
                      <ul className="text-sm text-text-secondary space-y-1 ml-6 list-disc">
                        {['communityMap1', 'communityMap2', 'communityMap3', 'communityMap4'].map((key) => (
                          <li key={key}>{t(`userManual.${key}`)}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Guidelines */}
            <div className="rounded-xl border border-dark-border/50 overflow-hidden bg-dark-bg/50">
              <button
                type="button"
                onClick={() => toggleSection('guidelines')}
                className="w-full flex items-center justify-between p-4 hover:bg-dark-card/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-red-500/20">
                    <Shield className="w-5 h-5 text-red-400" />
                  </div>
                  <span className="font-semibold text-text-primary">{t('userManual.guidelines')}</span>
                </div>
                {expandedSections.includes('guidelines') ? (
                  <ChevronUp className="w-5 h-5 text-text-muted" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-text-muted" />
                )}
              </button>
              {expandedSections.includes('guidelines') && (
                <div className="px-4 pb-4 border-t border-dark-border/30">
                  <div className="pt-4 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                        <h4 className="font-medium text-green-400 mb-2">{t('userManual.doTitle')}</h4>
                        <ul className="text-sm text-text-secondary space-y-1">
                          {['do1', 'do2', 'do3', 'do4', 'do5'].map((key) => (
                            <li key={key}>• {t(`userManual.${key}`)}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                        <h4 className="font-medium text-red-400 mb-2">{t('userManual.dontTitle')}</h4>
                        <ul className="text-sm text-text-secondary space-y-1">
                          {['dont1', 'dont2', 'dont3', 'dont4', 'dont5'].map((key) => (
                            <li key={key}>• {t(`userManual.${key}`)}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                      <p className="text-sm text-text-secondary">
                        <Trans
                          i18nKey="userManual.reporting"
                          components={{ 0: <strong className="text-amber-400" /> }}
                        />
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Languages */}
            <div className="rounded-xl border border-dark-border/50 overflow-hidden bg-dark-bg/50">
              <button
                type="button"
                onClick={() => toggleSection('languages')}
                className="w-full flex items-center justify-between p-4 hover:bg-dark-card/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-accent-purple/20">
                    <span className="text-xl">🌍</span>
                  </div>
                  <span className="font-semibold text-text-primary">{t('userManual.languagesSection')}</span>
                </div>
                {expandedSections.includes('languages') ? (
                  <ChevronUp className="w-5 h-5 text-text-muted" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-text-muted" />
                )}
              </button>
              {expandedSections.includes('languages') && (
                <div className="px-4 pb-4 border-t border-dark-border/30">
                  <div className="pt-4">
                    <p className="text-sm text-text-secondary mb-3">{t('userManual.languagesIntro')}</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                      {[
                        { flag: '🇺🇸', key: 'langEnglish' },
                        { flag: '🇪🇸', key: 'langSpanish' },
                        { flag: '🇫🇷', key: 'langFrench' },
                        { flag: '🇩🇪', key: 'langGerman' },
                      ].map(({ flag, key }) => (
                        <span
                          key={key}
                          className="px-3 py-2 rounded bg-dark-card/50 text-sm text-center"
                        >
                          {flag} {t(`userManual.${key}`)}
                        </span>
                      ))}
                    </div>
                    <GuideLanguageSelector variant="inline" className="mb-3" />
                    <p className="text-xs text-text-muted italic">{t('userManual.languagesTip')}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="relative px-6 py-4 border-t border-amber-500/20 bg-gradient-to-r from-amber-950/30 to-orange-950/20">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex flex-wrap gap-3 justify-center">
              <span className="text-sm text-text-muted">{t('userManual.quickLinks')}</span>
              <a href="/profile" className="text-sm text-amber-400 hover:text-amber-300 transition-colors underline underline-offset-2">
                {t('userManual.editProfile')}
              </a>
              <span className="text-text-muted hidden md:inline">•</span>
              <a href="/cultural-posts" className="text-sm text-amber-400 hover:text-amber-300 transition-colors underline underline-offset-2">
                {t('userManual.browsePosts')}
              </a>
              <span className="text-text-muted hidden md:inline">•</span>
              <a href="/events" className="text-sm text-amber-400 hover:text-amber-300 transition-colors underline underline-offset-2">
                {t('userManual.viewEvents')}
              </a>
              <span className="text-text-muted hidden md:inline">•</span>
              <a href="/community" className="text-sm text-amber-400 hover:text-amber-300 transition-colors underline underline-offset-2">
                {t('userManual.findMembers')}
              </a>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="px-6 py-2 rounded-lg bg-gradient-to-r from-amber-500 to-orange-600 text-white font-medium hover:from-amber-400 hover:to-orange-500 transition-all shadow-lg shadow-amber-500/20"
            >
              {t('userManual.gotIt')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserManual;
